/**
 * View String Modal
 * Modal dialog for previewing a string entry from a template (mirrors ViewConfigModal)
 */

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
  Divider
} from "@mui/material";
import { BglSecondarySmallButton } from "components/button/BglButtons";
import { StringRegistryItem } from "../types/stringRegistryTypes";

interface ViewStringModalProps {
  open: boolean;
  onClose: () => void;
  entry: StringRegistryItem | null;
  /** This template's per-locale overrides for the entry */
  localeOverrides?: { locale: string; value: string }[];
}

export const ViewStringModal: React.FC<ViewStringModalProps> = ({
  open,
  onClose,
  entry,
  localeOverrides
}) => {
  if (!entry) return null;

  const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div">{value || "-"}</Typography>
    </Box>
  );

  const overrides = localeOverrides || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        String Details
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {entry.displayName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <InfoRow
            label="String Resource ID"
            value={<code style={{ fontFamily: "monospace", color: "#1976d2" }}>{entry.stringResourceId}</code>}
          />

          <InfoRow label="Display Name" value={entry.displayName} />

          <InfoRow
            label="Status"
            value={
              <Chip
                label={entry.verificationStatus || "TODO"}
                size="small"
                color={
                  entry.verificationStatus === "APPROVED"
                    ? "success"
                    : entry.verificationStatus === "REJECTED"
                    ? "error"
                    : entry.verificationStatus === "READY_FOR_QA"
                    ? "warning"
                    : "default"
                }
              />
            }
          />

          {(entry.components?.length || 0) > 0 && (
            <InfoRow
              label="Components"
              value={
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {entry.components.map((c) => (
                    <Chip key={c.documentId} label={c.displayName || c.code} size="small" variant="outlined" />
                  ))}
                </Box>
              }
            />
          )}

          {(entry.sections?.length || 0) > 0 && (
            <InfoRow
              label="Sections"
              value={
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {entry.sections.map((s) => (
                    <Chip key={s.documentId} label={s.displayName || s.code} size="small" variant="outlined" />
                  ))}
                </Box>
              }
            />
          )}

          {entry.documentation && (
            <>
              <Divider sx={{ my: 2 }} />
              <InfoRow label="Documentation" value={entry.documentation} />
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <InfoRow
            label="Template Locale Overrides"
            value={
              overrides.length > 0 ? (
                <Box
                  sx={{
                    p: 1,
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75
                  }}
                >
                  {overrides.map((o) => (
                    <Box key={o.locale} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Chip label={o.locale} size="small" sx={{ minWidth: 56 }} />
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", pt: 0.25 }}
                      >
                        {o.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                "No overrides — inherits global values"
              )
            }
          />

          <Divider sx={{ my: 2 }} />
          <InfoRow label="Deprecated" value={entry.deprecated ? "⚠️ Yes" : "✓ No"} />
        </Box>
      </DialogContent>
      <DialogActions>
        <BglSecondarySmallButton onClick={onClose}>
          Close
        </BglSecondarySmallButton>
      </DialogActions>
    </Dialog>
  );
};
